import { Accordion } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  QuoteExpirationReminderForm,
  QuoteExpirationReminderSchema,
  ReminderIntervalSchema,
} from '@monetize/types/app';
import isNumber from 'lodash/isNumber';
import { useEffect } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { MdClose } from 'react-icons/md';
import { handleApiErrorToast } from '../../../../api/axios';
import {
  useGetQuoteExpirationReminder,
  useSaveQuoteExpirationReminder,
} from '../../../../api/settingsService';
import {
  MBox,
  MButton,
  MCustomIconButton,
  MCustomNumberInput,
  MFlex,
  MFormField,
  MLink,
  MPageContainer,
  MPageLoader,
  MSettingsPageHeader,
  MVStack,
} from '../../../../components/Monetize';
import { MSettingAccordionItem } from '../../../../components/Monetize/MSettingAccordionItem';
import {
  defaultQuoteExpirationReminder,
  EXPIRATION_REMINDER_TYPE_DISPLAY,
} from '../../../../constants/quoteExpirationReminder';
import { SETTINGS_CONTENT_MAX_WIDTH } from '../../../../constants/settings';
import { useAccordionControl } from '../../../../hooks';
import {
  getHighlightStyles,
  useSettingsSectionHighlight,
} from '../../../../hooks/useSettingsSectionHighlight';
import { useDocumentHead } from '../../../../services/documentHead';

export const QuoteExpirationReminder = () => {
  const { setDocTitle } = useDocumentHead();
  const isHighlighted = useSettingsSectionHighlight('quoteExpiration');
  const { accordionValue, handleAccordionChange, setAccordionValue } =
    useAccordionControl();

  const {
    control,
    formState: { isDirty, errors },
    setError,
    getValues,
    clearErrors,
    handleSubmit,
    setValue,
    reset,
    watch,
  } = useForm({
    resolver: zodResolver(QuoteExpirationReminderForm),
    defaultValues: {
      quoteExpirationReminders: [defaultQuoteExpirationReminder[0]],
    },
  });
  const isValid = Object.keys(errors).length === 0;
  const numberOfEmails = getValues('quoteExpirationReminders').length;

  const firstDayWatch = watch(`quoteExpirationReminders.0.days`);
  const secondDayWatch = watch(`quoteExpirationReminders.1.days`);
  const lastDayWatch = watch(`quoteExpirationReminders.2.days`);

  const { fields, append } = useFieldArray({
    control,
    name: 'quoteExpirationReminders',
    keyName: 'fieldArrayKey',
  });

  const { isLoading: isFetchLoading, data: quoteExpirationReminders } =
    useGetQuoteExpirationReminder();

  const { mutateAsync: mutateQuoteExpirationEmails, isPending: saveLoading } =
    useSaveQuoteExpirationReminder();

  useEffect(() => {
    if (quoteExpirationReminders?.length) {
      const controlledFields = defaultQuoteExpirationReminder
        .filter((defaultReminder) =>
          quoteExpirationReminders.find(
            (reminder) => reminder.type === defaultReminder.type,
          ),
        )
        .map((defaultReminder) => {
          const apiField: ReminderIntervalSchema | undefined =
            quoteExpirationReminders.find(
              (reminder) => reminder.type === defaultReminder.type,
            );
          return { ...defaultReminder, days: apiField?.days.toString() || '' };
        });
      reset({
        quoteExpirationReminders: [...controlledFields],
      });
    }
  }, [quoteExpirationReminders]);

  useEffect(() => {
    if (!isValid && (firstDayWatch || secondDayWatch || lastDayWatch)) {
      customValidate();
    }
  }, [isValid, firstDayWatch, secondDayWatch, lastDayWatch]);

  useEffect(() => {
    setDocTitle('Settings', 'Email Reminders');
  }, []);

  useEffect(() => {
    if (isHighlighted) {
      setAccordionValue(['0']);
    }
  }, [isHighlighted, setAccordionValue]);

  const handleBlur = () => {
    customValidate();
  };

  const appendRow = () => {
    const tempId = numberOfEmails;
    append({
      ...defaultQuoteExpirationReminder[tempId],
    });
  };

  const removeRow = (index: number) => {
    const reminders = getValues('quoteExpirationReminders');
    // remove all reminders after and including the selected reminder.
    // If we remove the second reminder we want to remove the last reminder if it exists
    reminders.splice(index);
    setValue('quoteExpirationReminders', reminders, { shouldDirty: true });
    clearErrors(`quoteExpirationReminders`);
  };

  const customValidate = () => {
    const emailReminderSettings = getValues().quoteExpirationReminders;
    emailReminderSettings.forEach((reminder, i) => {
      // clear errors as it persists even after removing row
      clearErrors(`quoteExpirationReminders.${i}.days`);

      // check for empty days values
      if (reminder.days === '') {
        setError(`quoteExpirationReminders.${i}.days`, {
          type: 'custom',
          message: 'Please enter a value for this day',
        });
        return;
      }
      const day = Number(reminder.days);
      // don't check "previousDay" if "day" isFirstDay
      const isFirstDay = i === 0;
      const previousDay =
        !isFirstDay && Number(emailReminderSettings[i - 1].days);

      if (!day) {
        return;
      } else if (!isFirstDay && !previousDay) {
        // ex. if we remove the 1st days value it doesn't make sense to have a 2nd days value
        setError(`quoteExpirationReminders.${i - 1}.days`, {
          type: 'custom',
          message:
            'Please enter a value for this day or delete the following days value',
        });
      } else if (!isFirstDay && isNumber(previousDay) && !(day < previousDay)) {
        // a days value cannot be more/earlier than the previous
        setError(`quoteExpirationReminders.${i}.days`, {
          type: 'custom',
          message: 'Value must be less than the previous day',
        });
      } else if (day < 1 || day > 30) {
        // a days value must be between 1 & 30
        setError(`quoteExpirationReminders.${i}.days`, {
          type: 'custom',
          message: 'Value must be between 1 and 30 days',
        });
      } else if (errors?.quoteExpirationReminders?.[i]?.days) {
        // clear errors
        clearErrors(`quoteExpirationReminders.${i}.days`);
      }
    });
  };

  const onSubmit = async () => {
    try {
      const formValues = getValues().quoteExpirationReminders;
      const data: QuoteExpirationReminderSchema = formValues.map((value) => ({
        days: value.days,
        type: value.type,
      }));

      await mutateQuoteExpirationEmails(data);
    } catch (error) {
      handleApiErrorToast(error);
    }
  };

  return (
    <MPageContainer alignItems="stretch">
      <MBox>
        <MSettingsPageHeader divider title="Email Reminders" />
      </MBox>

      <Accordion.Root
        id="quoteExpiration"
        multiple
        value={accordionValue}
        onValueChange={({ value }) => handleAccordionChange(value)}
        maxW={SETTINGS_CONTENT_MAX_WIDTH}
      >
        <MSettingAccordionItem
          value="0"
          description="The number of days (1-30) before a quote expires that we will send a reminder email. We will always send an email when a quote expires"
          label="Quote Expiration"
          {...getHighlightStyles(isHighlighted)}
          borderWidth="1px"
        >
          <MBox w="100%" pb={4}>
            {isFetchLoading && <MPageLoader />}
            {!isFetchLoading && (
              <form>
                <MVStack alignItems="start" my={6} gap={4}>
                  {fields.map((reminder, index) => {
                    const { type } = reminder;
                    return (
                      <MFormField
                        key={type}
                        error={errors?.quoteExpirationReminders?.[index]?.days}
                        label={EXPIRATION_REMINDER_TYPE_DISPLAY[type]}
                      >
                        <Controller
                          name={`quoteExpirationReminders.${index}.days`}
                          control={control}
                          render={({
                            field: { onChange, onBlur, value, ...rest },
                          }) => (
                            <MFlex alignItems="center">
                              <MCustomNumberInput
                                value={value}
                                inputMode="numeric"
                                placeholder="Enter Days"
                                onChange={(
                                  valueAsString: string,
                                  valueAsNumber: number,
                                ) => {
                                  onChange(valueAsString);
                                }}
                                onBlur={() => {
                                  onBlur();
                                  handleBlur();
                                }}
                                {...rest}
                              />
                              {index > 0 && (
                                <MCustomIconButton
                                  variant="icon"
                                  btnSize={4}
                                  justifySelf="self-end"
                                  color="tPurple.base"
                                  containerSize={6}
                                  iconProps={{
                                    _active: { color: 'unset' },
                                    _focus: { color: 'unset' },
                                  }}
                                  ml={4}
                                  icon={MdClose}
                                  aria-label="Remove reminder"
                                  onClick={() => removeRow(index)}
                                />
                              )}
                            </MFlex>
                          )}
                        />
                      </MFormField>
                    );
                  })}
                  {numberOfEmails < 3 && (
                    <MLink
                      mt={0}
                      color="tIndigo.base"
                      fontWeight="bold"
                      textDecoration="underline"
                      fontSize="sm"
                      onClick={() => appendRow()}
                    >
                      + Add Reminder
                    </MLink>
                  )}
                </MVStack>
              </form>
            )}
          </MBox>

          <MFlex justifyContent="flex-end">
            <MButton
              variant="primary"
              onClick={handleSubmit(onSubmit)}
              isDisabled={!isDirty || !isValid}
              isLoading={saveLoading}
            >
              Save
            </MButton>
          </MFlex>
        </MSettingAccordionItem>
      </Accordion.Root>
    </MPageContainer>
  );
};
